/**
 * 403 을 화면에 그대로 띄울 한 문장으로 바꾼다 — **서버가 준 사유(`data.reason`)로 말한다.**
 *
 * 2026-09-10 회신으로 교사 토큰이 v2 를 받게 되면서, 화면 곳곳의 「Manager 권한이 필요합니다」가
 * 거짓이 됐다(§5 「AI 트래커 403 문구 — 「Manager 권한」 단정 제거」). genetics 3종은 애초에
 * `user_required` 라 교사도 통과한다(§4-2) — 그러니 403 이 났을 때 그것을 등급 문제로 단정하면
 * 사용자는 있지도 않은 등급을 받으러 간다.
 *
 * **갈래를 사유로 가른다.** 회신 §1-3 이 못 박은 것은 두 종류의 403 이다:
 *   · `data.reason === 'out_of_scope'` — 게이트는 지났고 **담당·공유 범위 밖**이다(교사 토큰).
 *   · 사유 없음 — 게이트 자체의 403(문구 `Unauthorized user.`). 이 계정은 이 경로를 받지 못한다.
 * 서버가 「둘을 `data.reason` 유무로 가를 수 있다」고 적어 준 그 자리다.
 *
 * 세 번째 갈래를 둔 이유: 사유 코드가 늘면(회신의 사유 표는 계속 늘고 있다) 모르는 코드가
 * 위 둘 중 한쪽 문구를 조용히 입는다. 「담당이 아니다」도 「계정이 아니다」도 그때는 거짓일 수
 * 있으니, 아는 것만 단정하고 모르는 것은 참인 말만 한다 — roles.js 가 유형을 열거로 가르는 것과
 * 같은 이유다(모르는 값이 아무도 결정하지 않은 채 통과하지 않게).
 *
 * 세 갈래 모두 **「다시 로그인해도 풀리지 않습니다」**를 담는다. 403 은 401 과 다르다 —
 * 401 은 로그인하면 풀리고 403 은 계정·범위의 문제라 풀리지 않는데, 그 말이 없으면 사용자가
 * 로그아웃·로그인을 되풀이한다. 이 저장소가 ProblemHubV2/ProblemSearchPanel.jsx:113 에서
 * 이미 내린 같은 판단이고, 여기서 어휘를 맞춘다.
 * (out_of_scope 갈래는 「담당 선생님이나 관리자에게 요청」이 곧 그 말이라 문장으로 대신한다 —
 *  할 일이 정해진 자리에 「해도 안 된다」를 덧붙이면 다음 손이 흐려진다.)
 *
 * 이 파일이 `src/utils/` 에 있는 이유: 쓰는 세 자리(AITracker 페이지 · AITrackerContent 의
 * genetics/students·genetics/engine)가 **공용 apiUtils 를 지나지 않는 raw fetch** 라 기존 공통
 * 자리가 없다. 셋이 각자 문구를 가지면 한쪽만 고쳐지는 날이 온다.
 *
 * ⚠ ExamPapersV2/examPaperApi.js 의 `describeApiError` 와 합치지 않는다. 그쪽은 봉투를 살려 오는
 * 쓰기 헬퍼(requestJsonWithStatus)를 전제로 사유 표 열몇 갈래(409·423 포함)를 다루고, 여기는
 * 응답 봉투를 직접 까서 읽는 403 하나만 다룬다. 합치면 API 모듈을 안 쓰는 화면이 그 모듈을
 * 끌어오게 되고, 사유 표가 늘 때마다 트래커 문구까지 함께 흔들린다.
 */

/** 담당·공유 범위 밖(교사 토큰) — 게이트는 지났다. 할 일이 정해져 있어 그것으로 닫는다 */
const OUT_OF_SCOPE_TEXT =
  '담당 학생이 아닙니다 — 담당 강사나 관리자에게 요청하세요.';

/** 게이트 403 — 이 계정 자체가 이 화면을 받지 못한다. 로그인을 되풀이할 자리가 아니다 */
const GATE_TEXT =
  '이 계정으로는 볼 수 없습니다 — 관리자에게 문의하세요.';

/** 모르는 사유 — 참인 것만 말한다(막혔다 · 로그인으로는 안 풀린다 · 물어볼 곳) */
const UNKNOWN_REASON_TEXT =
  '접근할 수 없습니다 — 관리자에게 문의하세요.';

/**
 * @param {string|null|undefined} reason 403 봉투의 `data.reason`. 없으면 게이트 403 이다
 * @returns {string} 화면에 그대로 띄울 한 문장. **빈 문자열을 돌려주지 않는다** —
 *   호출부가 이것을 Error 의 message 로 쓰므로 빈 값이면 화면에 사유가 통째로 사라진다
 */
export default function forbiddenText(reason) {
  if (reason === 'out_of_scope') return OUT_OF_SCOPE_TEXT;
  // 빈 문자열·공백도 「사유 없음」으로 본다 — 봉투에 키만 있고 값이 빈 응답이 게이트 403 과 같은 처지다
  if (reason == null || String(reason).trim() === '') return GATE_TEXT;
  return UNKNOWN_REASON_TEXT;
}
